import { useQuery } from '@tanstack/react-query'
import { supabase } from '../config/supabase'
import { useAuth } from './useAuth'

export interface DailySummary {
  id: string
  undertaker_id: string | null
  chain_id: string | null
  summary_date: string
  profile_views: number
  profile_views_unique: number
  feed_impressions: number
  feed_impressions_unique: number
  feed_avg_position: number | null
  phone_reveals: number
  email_reveals: number
  website_clicks: number
  map_clicks: number
  views_from_search_result: number
  views_from_city_page: number
  views_from_region_page: number
  views_from_direct: number
  views_from_external: number
  views_from_search_engine: number
  views_from_prisberegner: number
  views_mobile: number
  views_desktop: number
  views_tablet: number
  prisberegner_impressions: number
  prisberegner_avg_position: number | null
  prisberegner_selections: number
  prisberegner_leads: number
  prisberegner_lead_charges: number
}

export interface AnalyticsBenchmark {
  id: string
  region: string | null
  benchmark_date: string
  avg_profile_views: number
  avg_phone_reveals: number
  avg_email_reveals: number
  avg_website_clicks: number
  avg_prisberegner_impressions: number
  avg_prisberegner_leads: number
  p50_profile_views: number
  p75_profile_views: number
  p90_profile_views: number
  p50_phone_reveals: number
  p75_phone_reveals: number
  p90_phone_reveals: number
}

export interface AggregatedMetrics {
  total_profile_views: number
  total_profile_views_unique: number
  total_feed_impressions: number
  total_feed_impressions_unique: number
  total_phone_reveals: number
  total_email_reveals: number
  total_website_clicks: number
  total_map_clicks: number
  total_prisberegner_impressions: number
  total_prisberegner_selections: number
  total_prisberegner_leads: number
  // Referrer breakdown
  views_from_search_result: number
  views_from_city_page: number
  views_from_region_page: number
  views_from_direct: number
  views_from_external: number
  views_from_search_engine: number
  views_from_prisberegner: number
  // Device breakdown
  views_mobile: number
  views_desktop: number
  views_tablet: number
}

// Date N days ago as YYYY-MM-DD in Danish time — summary_date is a Copenhagen-local
// date, so UTC (toISOString) would shift day boundaries by 1-2 hours
export function cphDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Copenhagen' })
}

// Fetch all matching summary rows in 1000-row pages (PostgREST caps a single
// request at ~1000 rows and truncates silently)
async function fetchSummaries(
  applyFilters: (query: any) => any
): Promise<DailySummary[]> {
  const all: DailySummary[] = []
  const pageSize = 1000
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await applyFilters(
      supabase.from('analytics_daily_summary').select('*')
    )
      .order('summary_date', { ascending: true })
      .order('id', { ascending: true }) // stable order so pages don't skip/duplicate rows
      .range(from, from + pageSize - 1)
    if (error) throw error
    all.push(...(data || []))
    if (!data || data.length < pageSize) break
  }
  return all
}

// Fetch daily summaries for a specific undertaker
export function useUndertakerAnalytics(undertakerId: string | null, days: number = 30) {
  return useQuery({
    queryKey: ['analytics', 'undertaker', undertakerId, days],
    queryFn: async () => {
      if (!undertakerId) return []
      return fetchSummaries((q) =>
        q.eq('undertaker_id', undertakerId).gte('summary_date', cphDaysAgo(days))
      )
    },
    enabled: !!undertakerId,
  })
}

// Fetch daily summaries for a specific chain (chain-PAGE events only — for chain
// totals across member locations use useChainMemberAnalytics)
export function useChainAnalytics(chainId: string | null, days: number = 30) {
  return useQuery({
    queryKey: ['analytics', 'chain', chainId, days],
    queryFn: async () => {
      if (!chainId) return []
      return fetchSummaries((q) =>
        q.eq('chain_id', chainId).gte('summary_date', cphDaysAgo(days))
      )
    },
    enabled: !!chainId,
  })
}

// Fetch daily summaries for all of a chain's member locations in one batched query.
// Covers 2x the window so callers can compute previous-period trends too.
export function useChainMemberAnalytics(memberIds: string[], days: number = 30) {
  return useQuery({
    queryKey: ['analytics', 'chain-members', memberIds, days],
    queryFn: async () => {
      if (memberIds.length === 0) return []
      return fetchSummaries((q) =>
        q.in('undertaker_id', memberIds).gte('summary_date', cphDaysAgo(days * 2))
      )
    },
    enabled: memberIds.length > 0,
  })
}

// Fetch all analytics for admin overview
export function useAdminAnalytics(days: number = 30) {
  const { isAdmin } = useAuth()

  return useQuery({
    queryKey: ['analytics', 'admin', days],
    queryFn: async () =>
      fetchSummaries((q) => q.gte('summary_date', cphDaysAgo(days))),
    enabled: isAdmin,
  })
}

// Fetch benchmarks for comparison
export function useBenchmarks(region?: string | null) {
  return useQuery({
    queryKey: ['analytics', 'benchmarks', region],
    queryFn: async () => {
      let query = supabase
        .from('analytics_benchmarks')
        .select('*')
        .order('benchmark_date', { ascending: false })
        .limit(1)
      
      if (region) {
        query = query.eq('region', region)
      } else {
        query = query.is('region', null)
      }
      
      const { data, error } = await query
      if (error) throw error
      return data?.[0] as AnalyticsBenchmark | undefined
    },
  })
}

// Helper to aggregate daily summaries into totals
export function aggregateSummaries(summaries: DailySummary[]): AggregatedMetrics {
  return summaries.reduce((acc, day) => ({
    total_profile_views: acc.total_profile_views + (day.profile_views || 0),
    total_profile_views_unique: acc.total_profile_views_unique + (day.profile_views_unique || 0),
    total_feed_impressions: acc.total_feed_impressions + (day.feed_impressions || 0),
    total_feed_impressions_unique: acc.total_feed_impressions_unique + (day.feed_impressions_unique || 0),
    total_phone_reveals: acc.total_phone_reveals + (day.phone_reveals || 0),
    total_email_reveals: acc.total_email_reveals + (day.email_reveals || 0),
    total_website_clicks: acc.total_website_clicks + (day.website_clicks || 0),
    total_map_clicks: acc.total_map_clicks + (day.map_clicks || 0),
    total_prisberegner_impressions: acc.total_prisberegner_impressions + (day.prisberegner_impressions || 0),
    total_prisberegner_selections: acc.total_prisberegner_selections + (day.prisberegner_selections || 0),
    total_prisberegner_leads: acc.total_prisberegner_leads + (day.prisberegner_leads || 0),
    views_from_search_result: acc.views_from_search_result + (day.views_from_search_result || 0),
    views_from_city_page: acc.views_from_city_page + (day.views_from_city_page || 0),
    views_from_region_page: acc.views_from_region_page + (day.views_from_region_page || 0),
    views_from_direct: acc.views_from_direct + (day.views_from_direct || 0),
    views_from_external: acc.views_from_external + (day.views_from_external || 0),
    views_from_search_engine: acc.views_from_search_engine + (day.views_from_search_engine || 0),
    views_from_prisberegner: acc.views_from_prisberegner + (day.views_from_prisberegner || 0),
    views_mobile: acc.views_mobile + (day.views_mobile || 0),
    views_desktop: acc.views_desktop + (day.views_desktop || 0),
    views_tablet: acc.views_tablet + (day.views_tablet || 0),
  }), {
    total_profile_views: 0,
    total_profile_views_unique: 0,
    total_feed_impressions: 0,
    total_feed_impressions_unique: 0,
    total_phone_reveals: 0,
    total_email_reveals: 0,
    total_website_clicks: 0,
    total_map_clicks: 0,
    total_prisberegner_impressions: 0,
    total_prisberegner_selections: 0,
    total_prisberegner_leads: 0,
    views_from_search_result: 0,
    views_from_city_page: 0,
    views_from_region_page: 0,
    views_from_direct: 0,
    views_from_external: 0,
    views_from_search_engine: 0,
    views_from_prisberegner: 0,
    views_mobile: 0,
    views_desktop: 0,
    views_tablet: 0,
  })
}

// Calculate trend percentage
export function calculateTrend(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0
  return Math.round(((current - previous) / previous) * 100)
}

// Split summaries into two periods for comparison.
// Splits by calendar date, not array midpoint — with sparse days an array split
// would compare unequal date spans.
export function splitSummariesForComparison(summaries: DailySummary[], days: number) {
  const cutoff = cphDaysAgo(Math.floor(days / 2))
  return {
    current: aggregateSummaries(summaries.filter((s) => s.summary_date >= cutoff)),
    previous: aggregateSummaries(summaries.filter((s) => s.summary_date < cutoff)),
  }
}

// Merge rows from multiple entities into one row per date (for trend charts)
export function mergeByDate(summaries: DailySummary[]): DailySummary[] {
  const byDate = new Map<string, DailySummary>()
  for (const row of summaries) {
    const existing = byDate.get(row.summary_date)
    if (!existing) {
      byDate.set(row.summary_date, { ...row })
      continue
    }
    for (const [key, value] of Object.entries(row)) {
      // ponytail: avg-position fields keep the first row's value — they aren't
      // charted anywhere; make them impression-weighted if that changes
      if (typeof value === 'number' && !key.includes('avg_position')) {
        ;(existing as any)[key] = ((existing as any)[key] || 0) + value
      }
    }
  }
  return Array.from(byDate.values()).sort((a, b) =>
    a.summary_date.localeCompare(b.summary_date)
  )
}

// Impression-weighted average feed position across days (null if no data)
export function averageFeedPosition(summaries: DailySummary[]): number | null {
  let weighted = 0
  let weight = 0
  for (const s of summaries) {
    if (s.feed_avg_position != null && s.feed_impressions > 0) {
      weighted += s.feed_avg_position * s.feed_impressions
      weight += s.feed_impressions
    }
  }
  return weight > 0 ? weighted / weight : null
}
